import React, { useEffect, useRef, useState } from 'react'
import Button from 'components/button'
import { Plus } from 'components/icons'
import ProjectSecretsList from 'views/settings/components/secrets/components/ProjectSecretsList'
import SecretDialog, { ProjectSecretFormValues } from 'views/settings/components/secrets/components/SecretDialog'
import { SETTINGS_CANVAS, SettingsCard } from 'views/settings/components/settingsCard'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { IProjectSecret } from 'types'
import useProject from 'hooks/useProject'
import { useShownHub } from 'hooks/useShownHub'

/**
 * The hub's secrets: values its scripts can read without them showing in a table. Only hub owners reach
 * this page (the settings page shows it to them alone, and the server refuses anyone else).
 */
const Secrets: React.FC = () => {
  const { displayErrorMessage } = useApplicationStore()
  const { project } = useProject()
  const projectId = project.publicId
  const [secrets, setSecrets] = useState<IProjectSecret[]>()
  const [loadFailed, setLoadFailed] = useState(false)
  const [showAddSecret, setShowAddSecret] = useState(false)
  // Which load is the latest, so only it fills the list: one still on its way from before (another hub, or
  // an earlier Try again) is dropped.
  const latestLoad = useRef(0)
  const stillShown = useShownHub(projectId)

  /** Fetches the list. `fresh` shows the loading rows first; after a change, the rows stay until it arrives. */
  const load = (fresh: boolean) => {
    // Asked for by a change started in the hub before, that finished after switching: not here.
    if (!stillShown(projectId)) return Promise.resolve()
    latestLoad.current += 1
    const thisLoad = latestLoad.current
    if (fresh) setSecrets(undefined)
    setLoadFailed(false)
    return api
      .getProjectSecrets(projectId)
      .then((response) => {
        if (thisLoad === latestLoad.current) setSecrets(response.data)
      })
      .catch(() => {
        if (thisLoad === latestLoad.current) setLoadFailed(true)
      })
  }

  // Another hub's settings: its own list, without a form left open for the last one.
  useEffect(() => {
    setShowAddSecret(false)
    if (projectId !== '') load(true)
  }, [projectId])

  const handleCreateSecret = (values: ProjectSecretFormValues) =>
    api
      .createProjectSecret(project, values.name, values.value)
      .then(() => {
        if (stillShown(project.publicId)) setShowAddSecret(false)
        return load(false)
      })
      .catch((error) => {
        if (stillShown(project.publicId)) displayErrorMessage(error)
      })

  const handleEditSecret = (secret: IProjectSecret, values: ProjectSecretFormValues, closeModal: () => void) =>
    api
      .updateProjectSecret(project, secret, values.name, values.value)
      .then(() => {
        closeModal()
        return load(false)
      })
      .catch((error) => {
        if (stillShown(project.publicId)) displayErrorMessage(error)
      })

  // Rejects when the delete fails, so the confirmation stays open to try again.
  const handleDeleteSecret = (secret: IProjectSecret) =>
    api.deleteProjectSecret(project, secret).then(
      () => load(false),
      (error) => {
        if (stillShown(project.publicId)) displayErrorMessage(error)
        throw error
      }
    )

  return (
    <div className={`w-full min-h-full overflow-auto ${SETTINGS_CANVAS}`}>
      <div className="flex flex-col gap-4 max-w-[960px] mx-auto">
        <SettingsCard
          title="Secrets"
          description="Values such as API keys that scripts in this hub can use without showing them. The webhook signing secret verifies webhook requests sent from this hub; it can be edited but not deleted."
        >
          <div className="flex justify-end px-4 py-3">
            <Button internalType="outline" data-tour="add-secret" onClick={() => setShowAddSecret(true)}>
              <Plus />
              Add Secret
            </Button>
          </div>
          <ProjectSecretsList
            key={projectId}
            secrets={secrets}
            loadFailed={loadFailed}
            onRetry={() => load(true)}
            onDelete={handleDeleteSecret}
            onEdit={handleEditSecret}
          />
        </SettingsCard>
      </div>

      {showAddSecret && <SecretDialog onClose={() => setShowAddSecret(false)} onSubmit={handleCreateSecret} />}
    </div>
  )
}

export default Secrets
